import { Amount, OutcomeDetails } from '@atomicfinance/bitcoin-dlc-provider';
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  useDisclosure,
} from '@chakra-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Paginator from '../../components/Paginator';
import StepSidebar from '../../components/StepSidebar';
import WalletUnlock from '../../components/WalletUnlock';
import config from '../../config/config';
import { persistBet } from '../../hooks/usePersistBet';
import { createClient, getClient } from '../../services/chainClient';
import { getTowerService } from '../../services/towerService';
import { RootState } from '../../store/reducers';
import { handle } from '../../utils/Utils';
import BetConfirmation, { Confirmation } from '../Bet/BetConfirmation';
import BetDeposit from '../Bet/BetDeposit';
import BetOverview from '../Bet/BetOverview';
import SetupInfluencerTwitter from '../SetupInfluencer/pages/SetupInfluencerTwitter';
import SetupInfluencerVerify from '../SetupInfluencer/pages/SetupInfluencerVerify';
import WalletInfo from '../Wallet/WalletInfo';
import WalletPassphrase from '../Wallet/WalletPassphrase';
import WalletPasswordSetup from '../Wallet/WalletPasswordSetup';
import BetDetails from './NewBetDetails';
import BetEventSelection from './NewBetEventSelection';
import NewBetLink from './NewBetLink';
import BetOdds from './NewBetOdds';

enum Step {
  BetEventSelection = 0,
  BetDetails,
  BetOdds,
  BetOverview,
  WalletInfo,
  WalletPasswordSetup,
  WalletPassphrase,
  WalletUnlock,
  TwitterInput,
  TwitterVerify,
  BetDeposit,
  BetConfirmation,
  BetComplete,
}

const StepHeaders = {
  [Step.BetEventSelection]: 'Bet Type',
  [Step.BetDetails]: 'Bet Details',
  [Step.BetOdds]: 'Bet Odds',
  [Step.BetOverview]: 'Bet Overview',
  [Step.WalletInfo]: 'Wallet Set-up',
  [Step.WalletPasswordSetup]: 'Wallet Set-up',
  [Step.WalletPassphrase]: 'Wallet Set-up',
  [Step.WalletUnlock]: 'Unlock Wallet',
  [Step.TwitterInput]: 'Twitter Verification',
  [Step.TwitterVerify]: 'Twitter Verification',
  [Step.BetDeposit]: 'Deposit Bet Amount',
  [Step.BetConfirmation]: 'Publish Bet',
  [Step.BetComplete]: 'Share Link',
};

const StepExtraInfo = {
  [Step.BetOdds]: {
    info:
      'Note: Total bet winnings you receive will be dependent on BTC network fees.',
  },
  [Step.WalletPasswordSetup]: {
    title: 'Why create a wallet password?',
    info:
      'Creating a password provides extra layer of security with your funds while allowing you to conveniently access your bets in future.',
  },
  [Step.WalletPassphrase]: {
    title: 'Access your bets on any browser?',
    info:
      'In the event where you login to Atomic Odds in a different browser or computer, you can always recover your bets and account by inputting the seed phrase when logging into the platform.',
  },
  [Step.BetComplete]: {
    info:
      'Once the counterparty accepts the bet you will have to confirm the bet within 36 hours in order to move the bet process further',
  },
};

export const ELECTION_RESOLUTION_DATE = new Date('2020-11-03T08:00:00.000Z');
export const ELECTION_REFUND_DATE = new Date('2021-01-30T00:00:00.000Z');

const createOffer = async (
  betAmount: number,
  betAmountCounter: number,
  event: string,
  prediction: string,
  inputs: any[],
  oracleSource: string,
  oracleDate: string,
  feePerByte?: number
) => {
  const bitcoin = getClient();

  // mainnet, testing, hardcoded

  // can use real mainnet oracle
  // can use real oracle but with testing mode
  // can use hard coded values

  let oracleInfo;
  let publishDate;
  console.log('oracleSource', oracleSource);

  if (oracleSource === 'hardcoded') {
    publishDate = new Date().toISOString();
    oracleInfo = {
      name: 'atomic.finance',
      publicKey:
        '03355cd87b61c0740a98509169e135e515d8cd6244e3c58d924f8455f414ef7a4b',
      rValue:
        '031f9fefc7c0be9b3cd14671590892ebf01c440034f1b193229bca3275a4217d89',
    };
  } else {
    if (oracleSource === 'mainnet') {
      console.log(
        'ELECTION_RESOLUTION_DATE',
        ELECTION_RESOLUTION_DATE.toISOString()
      );
      publishDate = ELECTION_RESOLUTION_DATE.toISOString();
    } else {
      // oracleSource === 'testing'
      publishDate = oracleDate;
    }

    const rValueResponse = await fetch(
      `${config.oracle.url}/asset/election/rvalue/${publishDate}?eventType=election`
    );
    let rValueData = await rValueResponse.json();
    console.log('rValueData', rValueData);

    oracleInfo = {
      name: 'atomic.finance',
      publicKey: rValueData.oraclePublicKey,
      rValue: rValueData.rvalue,
    };
  }

  const localCollateral = Amount.FromSatoshis(betAmount);
  const remoteCollateral = Amount.FromSatoshis(betAmountCounter);

  if (feePerByte) {
    const feeRate = feePerByte

    const inputDetails = {
      localCollateral,
      remoteCollateral,
      feeRate,
      maturityTime: new Date(
        new Date(publishDate).setHours(new Date().getHours() - 3)
      ),
      refundLockTime: ELECTION_REFUND_DATE.getTime(),
      cetCsvDelay: 0,
    };

    const outcomeDetailWin: OutcomeDetails = {
      localAmount: Amount.FromSatoshis(betAmount + betAmountCounter),
      remoteAmount: Amount.FromSatoshis(0),
      message: prediction === 'democratic' ? 'democratic' : 'republican',
    };

    const outcomeDetailLose: OutcomeDetails = {
      localAmount: Amount.FromSatoshis(0),
      remoteAmount: Amount.FromSatoshis(betAmount + betAmountCounter),
      message: prediction === 'democratic' ? 'republican' : 'democratic',
    };

    const outcomes: Array<OutcomeDetails> = [outcomeDetailWin, outcomeDetailLose];

    const towerInstance = getTowerService(bitcoin);
    const staticPubkey = (await towerInstance.initialize()).publicKey;

    const startingIndex = ((await towerInstance.getNumContracts(staticPubkey)) || 0) * 2;
    const offerMessage = await bitcoin.initializeContractAndOffer(
      inputDetails,
      outcomes,
      oracleInfo,
      startingIndex,
      inputs
    );

    const betDetails = {
      event,
      prediction,
      resolutionDate: new Date(publishDate),
      betSize: betAmount + betAmountCounter,
      betAmount,
      betAmountCounter,
    };

    return { offerMessage, betDetails };
  } else {
    throw Error('Fee Per Byte not set')
  }
};

const SIDEBAR_STEPS = {
  'Bet Type': Step.BetEventSelection,
  'Bet Details': Step.BetDetails,
  'Bet Odds': Step.BetOdds,
  'Wallet Set-up': Step.WalletUnlock,
  'Twitter Verification': Step.TwitterVerify,
  'Deposit Amount': Step.BetDeposit,
  'Bet Link': Step.BetConfirmation,
};

const NO_PAD = [Step.TwitterInput, Step.TwitterVerify];

const NewBet = () => {
  const history = useHistory();
  const [page, setPage] = useState<Step>(Step.BetEventSelection);

  const mnemonic = useSelector((state: RootState) => state.wallet.mnemonic);

  const [event] = useState<string>('election');
  const [prediction, setPrediction] = useState<string>('democratic');
  const [betAmount, _setBetAmount] = useState<number>(0);

  const [twitterInfo, setTwitterInfo] = useState<any | undefined>();

  const setBetAmount = (num) => _setBetAmount(parseInt(num));

  const [betOdds, setBetOdds] = useState<number>(1);
  const [secretFormVisible, setSecretFormVisible] = useState<
    'hidden' | 'visible'
  >('hidden');
  const [oracleSource, setOracleSource] = useState<string>('mainnet');
  const [oracleDate, setOracleDate] = useState(
    ELECTION_RESOLUTION_DATE.toISOString()
  );
  const betAmountCounter = Math.round(betAmount * betOdds);
  const betSize = betAmount + betAmountCounter;
  const [resolutionDate] = useState(ELECTION_RESOLUTION_DATE);

  const [offerGenerated, setOfferGenerated] = useState(false);
  const [offerPublished, setOfferPublished] = useState(false);
  const [offerGenerateError, setOfferGenerateError] = useState<string>();
  const [offerPublishError, setOfferPublishError] = useState<string>();
  const [pubkey, setPubkey] = useState<string>('');

  const {
    isOpen: isBetDetailsOpen,
    onOpen: onBetDetailsOpen,
    onClose: onBetDetailsClose,
  } = useDisclosure();

  const [betId, setBetId] = useState('');

  const alreadyDeposited = useRef(false);

  useEffect(() => {
    if (!mnemonic) return;
    (async () => {
      const client = createClient(mnemonic);
      setPubkey((await getTowerService(client).initialize()).publicKey);
    })();
  }, [mnemonic]);

  const onDeposit = async ({ inputs, feePerByte }) => {
    if (alreadyDeposited.current) return;
    alreadyDeposited.current = true;
    setPage(Step.BetConfirmation);

    const [offer, _offerGenerateError] = await handle(
      createOffer(
        betAmount,
        betAmountCounter,
        event,
        prediction,
        inputs,
        oracleSource,
        oracleDate,
        feePerByte
      )
    );

    if (!offer) {
      if (_offerGenerateError) {
        console.error(_offerGenerateError);
        setOfferGenerateError(_offerGenerateError.message);
      }
      return;
    }

    const { offerMessage, betDetails } = offer;

    setOfferGenerated(true);
    setBetId(offerMessage.contractId);

    // send offer to watchtower
    const [, _offerPublishError] = await handle(
      (await getTowerService(getClient()).initialize()).postOffer(offerMessage)
    );
    console.log(_offerPublishError);
    if (_offerPublishError) {
      setOfferPublishError(_offerPublishError.message);
      return;
    }

    setOfferPublished(true);

    await persistBet(offerMessage.contractId, betDetails);
  };

  const betConfirmations: Confirmation[] = [
    {
      label: 'Generating Bet Offer',
      completeLabel: 'Bet Offer Generated',
      complete: offerGenerated,
      error: offerGenerateError,
    },
    {
      label: 'Publishing Bet Offer',
      completeLabel: 'Bet Offer Published',
      complete: offerPublished,
      error: offerPublishError,
    },
  ];

  const handleOnWalletLoaded = async () => {
    if (localStorage.getItem('twitterInfo')) {
      setPage(Step.BetDeposit);
    } else {
      setPage(Step.TwitterInput);
    }
  };

  return (
    <>
      <Modal
        isOpen={isBetDetailsOpen}
        onClose={onBetDetailsClose}
        size="xl"
        isCentered
      >
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>Bet Details</ModalHeader>
            <ModalCloseButton onClick={onBetDetailsClose} />
            <ModalBody pb="1rem">
              <BetOverview
                onBack={() => {}}
                onNext={() => {}}
                prediction={prediction}
                betAmount={betAmount}
                betOdds={betOdds}
                betSize={betSize}
                resolutionDate={resolutionDate}
                showButtons={false}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>
      <div style={{ position: 'absolute', bottom: 0, left: 0, zIndex: 9 }}>
        <Button
          ml="auto"
          backgroundColor="#42486b"
          color="#42486b"
          onClick={() =>
            setSecretFormVisible(
              secretFormVisible === 'visible' ? 'hidden' : 'visible'
            )
          }
          style={{
            backgroundColor: '#42486b',
            color: '#42486b',
          }}
        >
          View
        </Button>
        <div style={{ visibility: secretFormVisible }}>
          <RadioGroup
            onChange={(e) => setOracleSource(e.toString())}
            value={oracleSource}
          >
            <Radio value="hardcoded">Hard Coded RValue</Radio>
            <Radio value="testing">Testing Oracle with Modified Date</Radio>
            <Radio value="mainnet">Mainnet Oracle</Radio>
          </RadioGroup>
          {oracleSource === 'testing' && (
            <>
              <FormLabel fontSize="lg">Oracle Date</FormLabel>
              <Input
                width="30em"
                value={oracleDate}
                onChange={(e) => setOracleDate(e.target.value)}
                placeholder="Oracle Date"
              />
            </>
          )}
        </div>
      </div>
      <Flex boxSize="full" position="relative">
        <StepSidebar steps={SIDEBAR_STEPS} step={page} />
        <Button
          bg="white"
          color="blue.600"
          borderColor="blue.600"
          borderWidth="1px"
          position="absolute"
          top="5"
          right="5"
          size="sm"
          colorScheme="white"
          onClick={() => history.push('/dashboard')}
        >
          Exit Bet Process
        </Button>
        <Flex
          flexDir="column"
          alignItems="center"
          justifyContent="center"
          p={8}
          boxSize="full"
          maxW="45em"
          mx="auto"
        >
          <Box w="100%">
            <Flex alignItems="center">
              <Heading mb={4} textTransform="uppercase">
                {StepHeaders[page]}
              </Heading>
              {page > Step.BetOverview && (
                <Text
                  ml="auto"
                  cursor="pointer"
                  textDecoration="underline"
                  onClick={onBetDetailsOpen}
                >
                  View Bet Details
                </Text>
              )}
            </Flex>

            {StepExtraInfo[page] && (
              <Flex
                p={2}
                w="full"
                bg="#646883"
                flexDir="column"
                color="white"
                borderRadius="4px"
                mb={4}
                fontSize="xs"
              >
                {StepExtraInfo[page].title && (
                  <Text fontWeight="500">{StepExtraInfo[page].title}</Text>
                )}
                <Text>{StepExtraInfo[page].info}</Text>
              </Flex>
            )}
            <Flex flex={1} alignItems="center" justifyContent="center">
              <Flex
                flexDir="column"
                w="full"
                boxShadow="0px 0px 14px 3px rgba(0, 0, 0, 0.07)"
                borderRadius="4px"
                p={NO_PAD.includes(page) ? '0' : '2rem'}
                backgroundColor="white"
              >
                <Paginator index={page}>
                  <BetEventSelection
                    onBack={() => history.push('/dashboard')}
                    onEventSelection={() => {
                      setPage(Step.BetDetails);
                    }}
                  />
                  <BetDetails
                    prediction={prediction}
                    setPrediction={setPrediction}
                    onBack={() => setPage(Step.BetEventSelection)}
                    onNext={() => setPage(Step.BetOdds)}
                  />
                  <BetOdds
                    onBack={() => setPage(Step.BetDetails)}
                    onNext={() => setPage(Step.BetOverview)}
                    prediction={prediction}
                    betAmount={betAmount}
                    setBetAmount={setBetAmount}
                    betOdds={betOdds}
                    setBetOdds={setBetOdds}
                  />
                  <BetOverview
                    onBack={() => setPage(Step.BetOdds)}
                    onNext={() => {
                      if (localStorage.getItem('encryptedMnemonic')) {
                        if (!mnemonic) {
                          setPage(Step.WalletUnlock);
                        } else {
                          if (localStorage.getItem('twitterInfo')) {
                            setPage(Step.BetDeposit);
                          } else {
                            setPage(Step.TwitterInput);
                          }
                        }
                      } else {
                        setPage(Step.WalletInfo);
                      }
                    }}
                    prediction={prediction}
                    betAmount={betAmount}
                    betOdds={betOdds}
                    betSize={betSize}
                    resolutionDate={resolutionDate}
                  />
                  <WalletInfo
                    onBack={() => setPage(Step.BetOverview)}
                    onNext={() => setPage(Step.WalletPasswordSetup)}
                  />
                  <WalletPasswordSetup
                    onBack={() => setPage(Step.BetOverview)}
                    onNext={() => setPage(Step.WalletPassphrase)}
                  />
                  <WalletPassphrase
                    onBack={() => setPage(Step.WalletPasswordSetup)}
                    onNext={handleOnWalletLoaded}
                  />
                  <WalletUnlock
                    onBack={() => setPage(Step.BetOverview)}
                    onNext={handleOnWalletLoaded}
                  />
                  <SetupInfluencerTwitter
                    onBack={() => {
                      setPage(Step.BetOverview);
                    }}
                    onNext={() => {
                      if (!twitterInfo) return;
                      setPage(Step.TwitterVerify);
                    }}
                    setTwitterInfo={setTwitterInfo}
                    twitterInfo={twitterInfo}
                  />
                  <SetupInfluencerVerify
                    onBack={() => {
                      setPage(Step.TwitterInput);
                    }}
                    onNext={() => setPage(Step.BetDeposit)}
                    twitterInfo={twitterInfo}
                    pubkey={pubkey}
                  />
                  <BetDeposit
                    onBack={() => setPage(Step.BetOverview)}
                    betAmount={betAmount}
                    onDeposit={onDeposit}
                  />
                  <BetConfirmation
                    confirmations={betConfirmations}
                    onNext={() => setPage(Step.BetComplete)}
                  />
                  <NewBetLink
                    betId={betId}
                    onNext={() => history.push('/dashboard')}
                    pubkey={pubkey}
                  />
                </Paginator>
              </Flex>
            </Flex>
          </Box>
        </Flex>
      </Flex>
    </>
  );
};

export default NewBet;
